
import { defineComponent, onMounted, PropType, reactive, ref } from 'vue'

import Loader from '@/common/components/UI/Loader/index.vue'
import { CmsInvoice, CmsInvoices } from '@/new-services/types/cms'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'
import { OktellWaitListItem } from '@/core/types/oktell.types'
import { oktell } from '@/core/realisations/oktell.realisation'
import useNotifications from '@/common/composable/useNotifications'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import InvoiceStatusRow from '@/modules/InvoiceModule/components/InvoiceStatusRow/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

type Column = {
    name: string
    key: string
}

const COLUMNS = [
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'state',
        name: 'Состояние',
    },
    {
        key: 'user',
        name: 'Кто проставил',
    },
    {
        key: 'clientType',
        name: 'Когда проставил',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'source',
        name: 'Источник создания',
    },
]

export default defineComponent({
    name: 'cms-invoices',
    components: { Loader, Cheap, NewModal, NewTable, InvoiceStatusRow, NewButton },
    props: {
        items: {
            type: Object as PropType<CmsInvoices>,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        failed: {
            type: Boolean,
            default: false,
        },
        columns: {
            type: Array as PropType<Column[]>,
            default: () => [],
        },
    },
    emits: ['refresh'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_open: false,
            is_loading: false,
            statuses_lo: [] as OktellWaitListItem[],
        })

        const getCount = (keySearch: string) => {
            let count = 0
            for (const [key, value] of Object.entries(props.items)) {
                if (keySearch === key) count = value.length
            }
            return count
        }

        const getItems = (keySearch: string) => {
            let items: CmsInvoice[] = []
            for (const [key, value] of Object.entries(props.items)) {
                if (keySearch === key) items = value
            }
            items.sort(
                (a, b) => new Date(a.predictDate).getTime() - new Date(b.predictDate).getTime()
            )
            return items
        }

        const getIcon = (typeId: number) => {
            return typeId === 1 ? 'fas fa-car' : 'fas fa-plane'
        }

        const getCheapClass = (id: number) => {
            if (LtlStatusColors[id]) return LtlStatusColors[id]
            else if (id === 51) return 'red'
            else if (![1, 4].includes(id)) return 'yellow'

            return ''
        }

        const getDateTitle = (item: CmsInvoice) => {
            if (!item.status) return '-'

            const code = item.status?.code

            if (item.isIntraCity && code === 205) return 'План. дата выдачи'
            else if (code === 201) return 'План. забора'
            else if (code === 202) return 'План. забора'
            else if (code === 203) return 'План. приемка'
            else if (code === 225) return 'План. обработки'
            else if (code === 205) return 'План. отгрузки'
            else if (code === 206) return 'План. прибытия'
            else if (code === 208) return 'План. дата выдачи'
            else if (code === 210) return 'План. дата доставки'
            else if (code === 211) return 'Дата доставки'

            return '-'
        }

        const formatDate = (dateStr: string | null) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            const dateSecond = dateStr.split(' ')[1]
            return dateFirst.split('-').reverse().join('.') + ' ' + dateSecond
        }

        const loadWaitList = async (id: number, orderId: number) => {
            try {
                state.is_loading = true

                const params = {
                    clientIds: [id, orderId],
                    isCompleted: 1,
                }
                const response = await oktell.getWaitList(params)
                state.statuses_lo = response.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке произошла ошибка',
                })
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const openLo = async (invoice: CmsInvoice) => {
            loadWaitList(invoice.id, invoice.orderId)
            state.is_open = true
        }

        const closeLo = () => (state.is_open = false)

        const refresh = () => emit('refresh')

        const scrollTop = ref<HTMLElement | null>(null)
        const scrollMain = ref<HTMLElement | null>(null)

        const syncScroll = (source: 'top' | 'main') => {
            if (!scrollTop.value || !scrollMain.value) return

            if (source === 'top') {
                scrollMain.value.scrollLeft = scrollTop.value.scrollLeft
            } else {
                scrollTop.value.scrollLeft = scrollMain.value.scrollLeft
            }
        }

        onMounted(() => {
            if (scrollTop.value && scrollMain.value) {
                const scrollbarContent = scrollTop.value.querySelector(
                    '.scrollbar-content'
                ) as HTMLElement | null
                if (scrollbarContent) {
                    scrollbarContent.style.width = `${scrollMain.value.scrollWidth}px`
                }
            }
        })

        return {
            scrollTop,
            scrollMain,
            syncScroll,
            state,
            getCount,
            getItems,
            getIcon,
            getCheapClass,
            getDateTitle,
            formatDate,
            openLo,
            closeLo,
            COLUMNS,
            refresh,
        }
    },
})
